import { PATHS } from "@/config/paths";
import {
  APP_NAME,
  LINKEDIN_URL,
  WHATSAPP_COMMUNITY_URL,
  X_URL,
  INSTAGRAM_URL,
  APP_STORE_URL,
  TIKTOK_URL,
  GOOGLE_PLAY_URL,
} from "@/config/constants";
import {
  AppleIcon,
  LinkedInIcon,
  XIcon,
  WhatsAppIcon,
  InstagramIcon,
  TikTokIcon,
  AndroidIcon,
} from "@/components/icons/Icons";
import { Logo } from "@/components/ui/logo";
import { DownloadButtons } from "@/components/ui/download-buttons";
import { Link } from "@tanstack/react-router";
import { CalendarHeart, Globe, Rss } from "lucide-react";
import { useMatchRoute } from "@tanstack/react-router";
import { useMobile } from "@/providers/mobileProvider";
import { useTranslation } from "@/hooks/useTranslation";
import { Heading } from "@/components/ui/heading";

export function Footer() {
  const currentYear = new Date().getFullYear();
  const matchRoute = useMatchRoute();
  const { isPWA } = useMobile();
  const { t } = useTranslation();

  return (
    <footer data-testid="footer" className="container py-16 grid grid-cols-12 gap-8">
      {/* Company & Legal */}
      <div className="space-y-4 col-span-12 lg:col-span-4 md:mr-16">
        <Link to={PATHS.HOME} className="flex items-center gap-2 text-xl">
          <Logo className="size-12 min-h-12 min-w-12" iconClassName="size-10" showAppName />
        </Link>

        <p className="text-muted-foreground max-w-sm">{t("appDescription")}</p>
        <div className="flex flex-wrap gap-3 pt-3">
          <DownloadButtons hideStartButton />
        </div>
      </div>

      {/* About Us */}
      <div className="col-span-12 grid grid-cols-3 gap-8 lg:col-span-8 md:mt-0 lg:grid-cols-3">
        <div className="space-y-4 col-span-3 md:col-span-1">
          <Link to={PATHS.ABOUT_US} className="block hover:underline">
            <Heading level="h3" size="h6" className="font-semibold">
              {t("footer.aboutUs")}
            </Heading>
          </Link>
          <div className="space-y-4">
            <Link
              to={PATHS.CONTACT}
              className={`block text-muted-foreground hover:text-foreground ${
                matchRoute({ to: PATHS.CONTACT }) ? "text-foreground font-semibold" : ""
              }`}
            >
              {t("footer.contact")}
            </Link>
            <Link
              to={PATHS.PRICING}
              className={`block text-muted-foreground hover:text-foreground ${
                matchRoute({ to: PATHS.PRICING }) ? "text-foreground font-semibold" : ""
              }`}
            >
              {t("footer.pricing")}
            </Link>
            <Link
              to={PATHS.BLOG}
              className={`block text-muted-foreground hover:text-foreground ${
                matchRoute({ to: PATHS.BLOG }) ? "text-foreground font-semibold" : ""
              }`}
            >
              {t("footer.blog")}
            </Link>
            <Link
              to={PATHS.CHANGELOG}
              className={`block text-muted-foreground hover:text-foreground ${
                matchRoute({ to: PATHS.CHANGELOG }) ? "text-foreground font-semibold" : ""
              }`}
            >
              {t("footer.changelog")}
            </Link>
            <Link
              to={PATHS.NEWSLETTER}
              className={`block text-muted-foreground hover:text-foreground ${
                matchRoute({ to: PATHS.NEWSLETTER }) ? "text-foreground font-semibold" : ""
              }`}
            >
              {t("footer.newsletter")}
            </Link>
            {/* <Link to={PATHS.COMPARE} className="block text-muted-foreground hover:text-foreground">
              {t("footer.compare")}
            </Link>
            <Link
              to={PATHS.AFFILIATE}
              className="block text-muted-foreground hover:text-foreground"
            >
              {t("footer.affiliateProgram")}
            </Link> */}
          </div>
        </div>

        {/* Tools */}
        <div className="space-y-4 col-span-3 md:col-span-1">
          <Link to={PATHS.TOOLS} className="block hover:underline">
            <Heading level="h3" size="h6" className="font-semibold">
              {t("footer.tools")}
            </Heading>
          </Link>
          <div className="space-y-4">
            {!isPWA && (
              <>
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                >
                  <AppleIcon className="size-4" />
                  {t("footer.iphoneApp")}
                </a>
                <a
                  href={GOOGLE_PLAY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                >
                  <AndroidIcon className="size-4" />
                  {t("footer.androidApp")}
                </a>
              </>
            )}

            <Link
              to={PATHS.DASHBOARD}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <Globe className="size-4" />
              {t("footer.webApp")}
            </Link>
            <a
              href="https://progresscalendars.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <CalendarHeart className="size-4" />
              {t("footer.progressCalendars")}
            </a>
            <a
              href="/rss.xml"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <Rss className="size-4" />
              RSS Feed
            </a>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-4 col-span-3 md:col-span-1">
          <Heading level="h3" size="h6" className="font-semibold">
            {t("footer.features")}
          </Heading>
          <nav className="space-y-2">
            <Link
              to={PATHS.CALENDAR}
              className={`block text-muted-foreground hover:text-foreground ${
                matchRoute({ to: PATHS.CALENDAR }) ? "text-foreground font-semibold" : ""
              }`}
            >
              {t("footer.healthCalendar")}
            </Link>
            <Link
              to={PATHS.ROUTINES}
              className={`block text-muted-foreground hover:text-foreground ${
                matchRoute({ to: PATHS.ROUTINES }) ? "text-foreground font-semibold" : ""
              }`}
            >
              {t("footer.healthRoutines")}
            </Link>
            <Link
              to={PATHS.GOALS}
              className={`block text-muted-foreground hover:text-foreground ${
                matchRoute({ to: PATHS.GOALS }) ? "text-foreground font-semibold" : ""
              }`}
            >
              {t("footer.healthGoals")}
            </Link>
            <Link
              to={PATHS.LIBRARY}
              className={`block text-muted-foreground hover:text-foreground ${
                matchRoute({ to: PATHS.LIBRARY }) ? "text-foreground font-semibold" : ""
              }`}
            >
              {t("footer.healthThemes")}
            </Link>
          </nav>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="border-t grid grid-cols-2 gap-8 col-span-12 md:mt-16 pt-8 text-muted-foreground">
        {/* Left Side: Copyright, Made With, Privacy, Terms, Google Play */}
        <div className="col-span-2 md:col-span-1 flex gap-1 text-sm">
          <p>
            {t("footer.copyright", { year: currentYear, appName: APP_NAME })}{" "}
            <Link
              to={PATHS.PRIVACY_POLICY}
              className={`hover:text-foreground ${
                matchRoute({ to: PATHS.PRIVACY_POLICY }) ? "text-foreground font-semibold" : ""
              }`}
            >
              {t("footer.privacyPolicy")}
            </Link>{" "}
            -{" "}
            <Link
              to={PATHS.TERMS_OF_SERVICE}
              className={`hover:text-foreground ${
                matchRoute({ to: PATHS.TERMS_OF_SERVICE }) ? "text-foreground font-semibold" : ""
              }`}
            >
              {t("footer.termsOfService")}
            </Link>
          </p>
        </div>

        {/* Right Side: Social Icons */}
        <div className="md:ml-auto col-span-2 md:col-span-1 flex items-center gap-12">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground"
            aria-label={t("footer.instagramProfile")}
          >
            <InstagramIcon className="fill-color-foreground dark:fill-white size-4" />
          </a>
          <a
            href={WHATSAPP_COMMUNITY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground"
            aria-label={t("footer.whatsappCommunity")}
          >
            <WhatsAppIcon className="fill-color-foreground dark:fill-white size-4" />
          </a>
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground"
            aria-label={t("footer.linkedinProfile")}
          >
            <LinkedInIcon className="fill-color-foreground dark:fill-white size-4" />
          </a>
          <a
            href={TIKTOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground"
            aria-label={t("footer.tiktokProfile")}
          >
            <TikTokIcon className="fill-color-foreground dark:fill-white size-4" />
          </a>
          <a
            href={X_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground"
            aria-label={t("footer.xProfile")}
          >
            <XIcon className="fill-color-foreground dark:fill-white size-4" />
          </a>
        </div>
      </div>
    </footer>
  );
}
